import React, { useEffect, useReducer, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import Swal from "sweetalert2";
import {
  addAccommodationBed,
  getAccommodationBedById,
  updateAccommodationBed,
} from "../../../services/AccommodationBedService";

const AddAccommodationBed = () => {
  const [loading, setLoading] = useState(false);
  const [accommodationBedName, setAccommodationBedName] = useState("");
  const hasFetched = useRef(false);
  const { id } = useParams();
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (id && !hasFetched.current) {
      hasFetched.current = true;
      const fetchAccommodationBed = async () => {
        try {
          setLoading(true);
          const response = await getAccommodationBedById(id);
          const accommodationBed = response?.data;
          setAccommodationBedName(accommodationBed?.data?.title);
        } catch (err) {
          console.error("Error fetching accommodation bed:", err);
        } finally {
          setLoading(false);
        }
      };
      fetchAccommodationBed();
    }
  }, [id]);

  const validateForm = () => {
    const newErrors = {};

    if (!accommodationBedName.trim()) {
      newErrors.accommodationBedName = "Title is required";
    }

    setErrors(newErrors);

    // Return true if no errors
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    try {
      // --- VALIDATE FORMS ---
      const formValid = validateForm();
      if (!formValid) {
        return;
      }
      const accommodationBedData = { title: accommodationBedName };

      setLoading(true);

      if (id) {
        // --- UPDATE EXISTING ACCOMMODATION BED ---
        await updateAccommodationBed(id, accommodationBedData);

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Accommodation bed details updated successfully",
        });
      } else {
        // --- CREATE NEW ACCOMMODATION BED ---
        const response = await addAccommodationBed(accommodationBedData);
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Accommodation bed added successfully",
          });
        }
      }
      navigate("/accommodation-bed", {
        state: {
          page: location.state?.page || 1,
          searchTerm: location.state?.searchTerm || "",
        },
      });
    } catch (err) {
      console.error("Error in handleSave:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Operation failed",
      });
    } finally {
      // hide loader
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div id="preloader">
        <div className="sk-three-bounce">
          <div className="sk-child sk-bounce1"></div>
          <div className="sk-child sk-bounce2"></div>
          <div className="sk-child sk-bounce3"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageTitle
        activeMenu={`${id ? "Edit Accommodation Bed" : "Add Accommodation Bed"
          }`}
        motherMenu="Accommodation Bed"
        activeMenuLink={`${id ? `edit-accommodation-bed/${id}` : "add-accommodation-bed"
          }`}
        motherMenuLink={"accommodation-bed"}
      />
      <div className="row">
        <div className="col-xl-12">
          <div className="row">
            <div className="col-xl-8 col-xxl-12">
              <div className="card h-auto">
                <div className="card-body">
                  <form>
                    <div
                      onClick={() => {
                        navigate("/accommodation-bed", {
                          state: {
                            page: location.state?.page || 1,
                            searchTerm: location.state?.searchTerm || "",
                          },
                        });
                      }}
                      className="mb-4 d-flex align-items-center flex-wrap gap-2 cursor-pointer w-max"
                    >
                      <i className="fas fa-chevron-left"></i>
                      <div>Accommodation Bed List</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="title">Title</label>
                      <input
                        id="title"
                        type="text"
                        className="form-control w-full"
                        placeholder="Title"
                        value={accommodationBedName}
                        onChange={(e) =>
                          setAccommodationBedName(e.target.value)
                        }
                      />
                      {errors.accommodationBedName && (
                        <div className="text-danger mt-1" id="title-error">
                          {errors.accommodationBedName}
                        </div>
                      )}
                    </div>
                  </form>
                </div>
              </div>
              <hr style={{ margin: "0px" }} className="mx-2" />
              <div className="card-footer border-0 text-end py-3 d-flex justify-content-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    navigate("/accommodation-bed", {
                      state: {
                        page: location.state?.page || 1,
                        searchTerm: location.state?.searchTerm || "",
                      },
                    })
                  }}
                >
                  Back
                </Button>

                {/* Save / Update Button */}
                <Button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleSave}
                >
                  {id ? "Update" : "Save"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddAccommodationBed;
